import type {
  AdminBadge,
  UpdateAdminBadgeBody,
} from '@/api/(admin)/badges/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Pure RPC over the admin badge endpoints: the two reads on `/api/badges` and
 * the update on `/api/admin/badges/:id`, all on `API_ROOT_URL`.
 */

/**
 * The full catalogue, in catalogue order. Unpaginated, and without
 * `claimingRequirements`; unwraps the `{ data, metadata }` envelope.
 */
export async function fetchAdminBadges(): Promise<AdminBadge[]> {
  const { data } = await apiFetch<{ data: AdminBadge[] }>(
    '/api/badges',
    {},
    API_ROOT_URL,
  );
  return data;
}

/**
 * One badge, including `claimingRequirements`, `translations` and the CDN image
 * url. Migrated to `/api/badges/:id`; unwraps its `{ data, metadata }` envelope.
 */
export async function fetchAdminBadge(id: string): Promise<AdminBadge> {
  const { data } = await apiFetch<{ data: AdminBadge }>(
    `/api/badges/${encodeURIComponent(id)}`,
    {},
    API_ROOT_URL,
  );
  return data;
}

/**
 * Saves an edit, gated behind `admin-badges:update`. The image is deliberately
 * not part of the payload — the old dashboard could not change it either.
 * Migrated to `/api/admin/badges/:id`, so it takes `API_ROOT_URL`; answers the
 * `{ data, metadata }` envelope as-is.
 */
export async function updateAdminBadge(
  id: string,
  body: UpdateAdminBadgeBody,
): Promise<{ data: AdminBadge }> {
  return apiFetch<{ data: AdminBadge }>(
    `/api/admin/badges/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
